import { ASSIST_API, requestParams } from 'services/assist-api/axios';
import type { AssistQuery } from 'services/assist-api/query';
import { useQueryAssist } from 'services/assist-api/query-client';

export type BaseMetadata = API_JSON & {
  name: string;
  value: string;
};

export type CategoryMetadata = {
  id: number;
  name: string;
  value: string;
  categoryId: number;
};

const order = { 'order[order]': 'asc' } as const;
const groups = ['metadata-category'];
const include = 'category';

/**
 * Fetch metadatas
 * @param params can be used to customise/limit the metadatas retrieved
 * @returns an array of metadatas
 */
export const getMetadatas: GetMany<BaseMetadata> = async (params) =>
  (await ASSIST_API.get('metadata', { params: requestParams({ order, groups, include }, params) }))
    .data;

// Todo: Setting the return type from any breaks code in getTestItemsFull. Should be refactored.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getMetadatasByCategoryIds: any = async (metadataIds, params?) =>
  (
    await ASSIST_API.get('metadata', {
      params: requestParams(
        { order, groups, include },
        { ...params, filters: { category: metadataIds } }
      )
    })
  ).data;

/**
 * Fetch metadata (singular)
 * @param params can be used to customise/limit the metadatas retrieved
 * @returns a single metadata
 */
export const getMetadata: GetOne<BaseMetadata> = async (id, params) =>
  (
    await ASSIST_API.get(`metadata/${id}`, {
      params: requestParams({ order, groups, include }, params)
    })
  ).data;

/**
 * Hook implementation to fetch metadatas
 *  - caches data
 *  - loading, error, etc
 * @param params additional options to filter the request
 * @returns metadatas matching request
 */
export const useMetadatas: AssistQuery.UseMany<BaseMetadata> = (params, options) =>
  useQueryAssist(['metadata', params], () => getMetadatas(params), {
    staleTime: STALE_LONG,
    ...options
  });

/**
 * Given an metadataId fetch and return the metadata
 *  - only runs once metadataId is provided
 *  - fetches the one metadata
 *  - caches data
 * @param id metadataId
 * @param params optional parameters to change requested data
 * @returns the matching metadata
 */
export const useMetadata: AssistQuery.UseOne<BaseMetadata> = (id, params, options) =>
  useQueryAssist(['metadata', { id, ...params }], () => getMetadata(id, params), {
    staleTime: STALE_LONG,
    ...options,
    enabled: !!id
  });
